/**
 * pageMetadata.js
 *
 * Single source of truth for AI-search optimization metadata on tool and
 * marketing pages: BreadcrumbList items and HowTo JSON-LD bodies.
 *
 * Keyed by tool/page slug. Imported by `<ToolPageMeta />` and by individual
 * marketing pages that just need a `<Breadcrumb>`.
 */
import { SITE_DOMAIN } from "../components/SEO";

const TOOLS_HUB = { name: "Free tools", to: "/tools" };
const HOME = { name: "Home", to: "/" };

/* -------------------------------- Breadcrumb -------------------------------- */
// Each entry is the FULL ordered list of crumbs ending at the current page.
export const BREADCRUMBS = {
    // Marketing pages
    about: [HOME, { name: "About", to: "/about" }],
    pricing: [HOME, { name: "Pricing", to: "/pricing" }],
    zeroconf: [HOME, { name: "ZeroConf Firewall Configurator", to: "/zeroconf" }],
    ipam: [HOME, { name: "Client Data Suite (IPAM)", to: "/ipam" }],
    tools: [HOME, TOOLS_HUB],

    // Tool pages
    "subnet-calculator": [HOME, TOOLS_HUB, { name: "Subnet Calculator", to: "/tools/subnet-calculator" }],
    "dns-health": [HOME, TOOLS_HUB, { name: "DNS Health Check", to: "/tools/dns-health" }],
    "cve-lookup": [HOME, TOOLS_HUB, { name: "CVE Lookup", to: "/tools/cve-lookup" }],
    "tls-inspector": [HOME, TOOLS_HUB, { name: "TLS / SSL Inspector", to: "/tools/tls-inspector" }],
    "security-headers": [HOME, TOOLS_HUB, { name: "Security Headers Analyzer", to: "/tools/security-headers" }],
    "password-breach": [HOME, TOOLS_HUB, { name: "Password Breach Check", to: "/tools/password-breach" }],
    "nis2-assessment": [HOME, TOOLS_HUB, { name: "NIS2 Readiness Assessment", to: "/tools/nis2-assessment" }],
};

/* ----------------------------------- HowTo --------------------------------- */
const browserTool = { "@type": "HowToTool", name: "Web browser" };
const noSignup = (name) => ({
    "@type": "HowToTool",
    name: `${name} (free, no signup)`,
});

export const HOWTO_SCHEMAS = {
    "subnet-calculator": {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to calculate an IPv4 subnet with Cybersecurity Forefront",
        description:
            "Quickly compute network address, broadcast, usable host range, and netmask for any IPv4 CIDR block using our free browser-based subnet calculator.",
        totalTime: "PT30S",
        tool: [browserTool, noSignup("Cybersecurity Forefront Subnet Calculator")],
        step: [
            {
                "@type": "HowToStep",
                position: 1,
                name: "Open the subnet calculator",
                text: "Navigate to https://cybersecurityforefront.com/tools/subnet-calculator in any browser. No signup is required.",
                url: `${SITE_DOMAIN}/tools/subnet-calculator`,
            },
            {
                "@type": "HowToStep",
                position: 2,
                name: "Enter your IPv4 address and CIDR",
                text: "Type the network address (for example 10.10.0.0) into the input field, then pick a CIDR like /24 from the dropdown. The calculator validates the IP on every keystroke.",
            },
            {
                "@type": "HowToStep",
                position: 3,
                name: "Read the calculated values",
                text: "The result card instantly shows the network address, broadcast address, subnet mask, wildcard mask, first and last usable host, total usable hosts, and the IPv4 address class.",
            },
            {
                "@type": "HowToStep",
                position: 4,
                name: "Copy the summary",
                text: "Click 'Copy result' to put a plain-text summary on your clipboard, ready to paste into a ticket or runbook.",
            },
        ],
    },
    "dns-health": {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to audit DNS health (SPF, DMARC, DKIM, DNSSEC) with Cybersecurity Forefront",
        description:
            "Run a free DNS hygiene audit on any domain: SPF, DMARC, DKIM, MX, NS, DNSSEC, MTA-STS, plus 4 major blacklist lookups, with remediation callouts.",
        totalTime: "PT45S",
        tool: [browserTool, noSignup("Cybersecurity Forefront DNS Health Check")],
        step: [
            {
                "@type": "HowToStep",
                position: 1,
                name: "Open the DNS Health Check",
                text: "Navigate to https://cybersecurityforefront.com/tools/dns-health.",
                url: `${SITE_DOMAIN}/tools/dns-health`,
            },
            {
                "@type": "HowToStep",
                position: 2,
                name: "Enter a domain to audit",
                text: "Type the apex domain (for example example.com — without https:// or www) into the input field.",
            },
            {
                "@type": "HowToStep",
                position: 3,
                name: "Run the audit",
                text: "Click 'Audit'. The tool queries authoritative resolvers and grades each record category in a result card.",
            },
            {
                "@type": "HowToStep",
                position: 4,
                name: "Apply the remediation",
                text: "Each failed check shows the suggested DNS record value. Copy it into your DNS provider's control panel and re-run the audit to confirm.",
            },
        ],
    },
    "cve-lookup": {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to look up CVE vulnerabilities for firewalls and network appliances",
        description:
            "Search the NIST National Vulnerability Database (NVD) by CVE ID, vendor, or product. Returns CVSS score, severity, affected versions, and patched versions.",
        totalTime: "PT20S",
        tool: [browserTool, noSignup("Cybersecurity Forefront CVE Lookup")],
        step: [
            {
                "@type": "HowToStep",
                position: 1,
                name: "Open the CVE Lookup tool",
                text: "Navigate to https://cybersecurityforefront.com/tools/cve-lookup.",
                url: `${SITE_DOMAIN}/tools/cve-lookup`,
            },
            {
                "@type": "HowToStep",
                position: 2,
                name: "Pick a search mode",
                text: "Choose CVE ID (for example CVE-2024-21733), vendor (Fortinet, WatchGuard, Zyxel, Netgate), or product.",
            },
            {
                "@type": "HowToStep",
                position: 3,
                name: "Enter your query and submit",
                text: "Type the identifier and press Search. The tool queries the NIST NVD live.",
            },
            {
                "@type": "HowToStep",
                position: 4,
                name: "Review the result",
                text: "Read the CVSS v3 base score, severity label, affected and patched versions, and click external references for the vendor advisory.",
            },
        ],
    },
    "tls-inspector": {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to inspect a TLS / SSL certificate and grade an HTTPS endpoint",
        description:
            "Validate a TLS certificate chain, enumerate supported TLS protocols (1.0 to 1.3) and cipher suites, verify HSTS, and assign an A to F grade.",
        totalTime: "PT60S",
        tool: [browserTool, noSignup("Cybersecurity Forefront TLS / SSL Inspector")],
        step: [
            {
                "@type": "HowToStep",
                position: 1,
                name: "Open the TLS / SSL Inspector",
                text: "Navigate to https://cybersecurityforefront.com/tools/tls-inspector.",
                url: `${SITE_DOMAIN}/tools/tls-inspector`,
            },
            {
                "@type": "HowToStep",
                position: 2,
                name: "Enter the hostname",
                text: "Type the hostname, optionally with a port (for example example.com or example.com:8443).",
            },
            {
                "@type": "HowToStep",
                position: 3,
                name: "Run the probe",
                text: "Click 'Inspect'. The tool probes every TLS version and pulls the full certificate chain.",
            },
            {
                "@type": "HowToStep",
                position: 4,
                name: "Read the grade and remediation",
                text: "Review the A to F grade, the protocol matrix, the cipher suites list, and the per-finding remediation tips. Apply the suggested changes to your web server or CDN.",
            },
        ],
    },
    "security-headers": {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to grade your HTTP security headers (CSP, HSTS, X-Frame-Options)",
        description:
            "Audit any URL's security headers — Content-Security-Policy, HSTS, X-Frame-Options, Referrer-Policy, Permissions-Policy — and get a score out of 100 with specific remediation.",
        totalTime: "PT30S",
        tool: [browserTool, noSignup("Cybersecurity Forefront Security Headers Analyzer")],
        step: [
            {
                "@type": "HowToStep",
                position: 1,
                name: "Open the Security Headers Analyzer",
                text: "Navigate to https://cybersecurityforefront.com/tools/security-headers.",
                url: `${SITE_DOMAIN}/tools/security-headers`,
            },
            {
                "@type": "HowToStep",
                position: 2,
                name: "Enter the URL to analyze",
                text: "Paste the full URL (for example https://example.com) into the input.",
            },
            {
                "@type": "HowToStep",
                position: 3,
                name: "Run the analysis",
                text: "Click 'Analyze'. The tool fetches the URL, parses the response headers, and computes a score out of 100.",
            },
            {
                "@type": "HowToStep",
                position: 4,
                name: "Apply the recommended headers",
                text: "Copy the recommended header values from the remediation panel into your web server or CDN configuration. Re-run to verify.",
            },
        ],
    },
    "password-breach": {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to check if a password is in a known data breach (k-anonymity)",
        description:
            "Privacy-first lookup against 850M+ leaked password hashes using k-anonymity. Your password never leaves the browser — only the first 5 hex characters of its SHA-1 hash are sent to the lookup API.",
        totalTime: "PT15S",
        tool: [browserTool, noSignup("Cybersecurity Forefront Password Breach Check")],
        step: [
            {
                "@type": "HowToStep",
                position: 1,
                name: "Open the Password Breach Check",
                text: "Navigate to https://cybersecurityforefront.com/tools/password-breach.",
                url: `${SITE_DOMAIN}/tools/password-breach`,
            },
            {
                "@type": "HowToStep",
                position: 2,
                name: "Type the password you want to check",
                text: "Type or paste the password into the input field. The check runs locally — the SHA-1 hash is computed in your browser and only the first 5 hex characters are sent to the HaveIBeenPwned API.",
            },
            {
                "@type": "HowToStep",
                position: 3,
                name: "Read the result",
                text: "If the password is in a known breach, the card shows the number of times it appears. Treat any breached password as compromised and rotate it everywhere.",
            },
        ],
    },
    "nis2-assessment": {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "How to run a NIS2 self-assessment and download a PDF readiness report",
        description:
            "15-question self-assessment aligned to NIS2 Article 21 risk management measures. Generates a weighted score plus a downloadable PDF roadmap.",
        totalTime: "PT5M",
        tool: [browserTool, noSignup("Cybersecurity Forefront NIS2 Readiness Assessment")],
        step: [
            {
                "@type": "HowToStep",
                position: 1,
                name: "Open the NIS2 Readiness Assessment",
                text: "Navigate to https://cybersecurityforefront.com/tools/nis2-assessment.",
                url: `${SITE_DOMAIN}/tools/nis2-assessment`,
            },
            {
                "@type": "HowToStep",
                position: 2,
                name: "Answer the 15 questions honestly",
                text: "Each question takes 10 to 20 seconds and covers a NIS2 Article 21 measure: governance, supply chain, business continuity, incident handling, cryptography & MFA, vulnerability management, training, and basic hygiene.",
            },
            {
                "@type": "HowToStep",
                position: 3,
                name: "Submit and review your readiness dashboard",
                text: "The tool computes a weighted 0 to 100 score plus a per-pillar breakdown so you can see exactly where you stand.",
            },
            {
                "@type": "HowToStep",
                position: 4,
                name: "Download the PDF roadmap",
                text: "Click 'Download PDF' for a printable report — useful as a quick deliverable for the board or input to a full NIS2 gap analysis. No signup required.",
            },
        ],
    },
};
